import type { TileRenderState } from './types';

// Uniform buffer layout (must match terrain.wgsl Uniforms struct):
// view_proj: mat4x4<f32>    (64 bytes, offset 0)
// camera_pos: vec3<f32>     (12 bytes, offset 64)
// _pad0: f32                (4 bytes, offset 76)
// light_dir: vec3<f32>      (12 bytes, offset 80)
// _pad1: f32                (4 bytes, offset 92)
// elev_min: f32             (4 bytes, offset 96)
// elev_max: f32             (4 bytes, offset 100)
// elev_scale: f32           (4 bytes, offset 104)
// texel_size: f32           (4 bytes, offset 108)
// wireframe: u32            (4 bytes, offset 112)
// has_detail: u32           (4 bytes, offset 116)
// detail_origin_u: f32      (4 bytes, offset 120)
// detail_origin_v: f32      (4 bytes, offset 124)
// detail_scale: f32         (4 bytes, offset 128)
// implicit pad              (12 bytes, offset 132 → 144, struct align 16)
// Total: 144 bytes

const UNIFORM_SIZE = 144;

// Reusable scratch buffer for writing uniforms — avoids per-tile-per-frame allocation
const _uniformBuf = new ArrayBuffer(UNIFORM_SIZE);
const _uniformF32 = new Float32Array(_uniformBuf);
const _uniformU32 = new Uint32Array(_uniformBuf);

export class Renderer {
  device!: GPUDevice;
  queue!: GPUQueue;
  context!: GPUCanvasContext;
  format!: GPUTextureFormat;
  depthTexture!: GPUTexture;
  depthView!: GPUTextureView;
  pipeline!: GPURenderPipeline;
  bindGroupLayout!: GPUBindGroupLayout;
  sampler!: GPUSampler;
  dummyDetailView!: GPUTextureView;

  private width = 0;
  private height = 0;

  async init(canvas: HTMLCanvasElement): Promise<void> {
    const adapter = await navigator.gpu.requestAdapter({
      powerPreference: 'high-performance',
    });
    if (!adapter) throw new Error('No WebGPU adapter found');

    this.device = await adapter.requestDevice({
      requiredLimits: {
        maxStorageBufferBindingSize: 256 * 1024 * 1024,
        maxBufferSize: 256 * 1024 * 1024,
      },
    });
    this.queue = this.device.queue;

    this.context = canvas.getContext('webgpu')!;
    this.format = navigator.gpu.getPreferredCanvasFormat();
    this.context.configure({
      device: this.device,
      format: this.format,
      alphaMode: 'premultiplied',
    });

    this.width = canvas.width;
    this.height = canvas.height;

    this.createDepthTexture();

    // 1×1 dummy detail texture for tiles without a detail patch
    const dummyTex = this.device.createTexture({
      size: { width: 1, height: 1 },
      format: 'r16float',
      usage: GPUTextureUsage.TEXTURE_BINDING,
    });
    this.dummyDetailView = dummyTex.createView();

    // Bind group layout: uniform, texture_2d (base), sampler, texture_2d (detail)
    this.bindGroupLayout = this.device.createBindGroupLayout({
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          sampler: { type: 'filtering' },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
      ],
    });

    this.sampler = this.device.createSampler({
      magFilter: 'linear',
      minFilter: 'linear',
      addressModeU: 'clamp-to-edge',
      addressModeV: 'clamp-to-edge',
    });

    // Load terrain shader
    const shaderCode = await fetch('shaders/terrain.wgsl').then(r => r.text());
    const shaderModule = this.device.createShaderModule({ code: shaderCode });

    const pipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [this.bindGroupLayout],
    });

    this.pipeline = this.device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: {
        module: shaderModule,
        entryPoint: 'vs_main',
        buffers: [{
          arrayStride: 20, // 3 floats pos + 2 floats uv = 5 * 4
          attributes: [
            { shaderLocation: 0, offset: 0, format: 'float32x3' },
            { shaderLocation: 1, offset: 12, format: 'float32x2' },
          ],
        }],
      },
      fragment: {
        module: shaderModule,
        entryPoint: 'fs_main',
        targets: [{ format: this.format }],
      },
      primitive: {
        topology: 'triangle-list',
        frontFace: 'ccw',
        cullMode: 'none',
      },
      depthStencil: {
        format: 'depth32float',
        depthWriteEnabled: true,
        depthCompare: 'less',
      },
    });
  }

  private createDepthTexture(): void {
    if (this.depthTexture) this.depthTexture.destroy();
    this.depthTexture = this.device.createTexture({
      size: { width: this.width, height: this.height },
      format: 'depth32float',
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });
    this.depthView = this.depthTexture.createView();
  }

  resize(width: number, height: number): void {
    if (width === this.width && height === this.height) return;
    this.width = width;
    this.height = height;
    this.createDepthTexture();
  }

  /** Write per-tile uniforms into the tile's uniform buffer. */
  writeUniforms(
    tile: TileRenderState,
    vp: Float32Array,
    cameraPosX: number, cameraPosY: number, cameraPosZ: number,
    elevScale: number,
  ): void {
    const f32 = _uniformF32;
    const u32 = _uniformU32;

    // view_proj: 16 floats at offset 0
    f32.set(vp, 0);
    // camera_pos at offset 16 (in f32 units)
    f32[16] = cameraPosX;
    f32[17] = cameraPosY;
    f32[18] = cameraPosZ;
    // light_dir at offset 20
    f32[20] = 0.3;
    f32[21] = 1.0;
    f32[22] = 0.5;
    // elev_min/max/scale/texel_size at offset 24
    f32[24] = tile.elevMin;
    f32[25] = tile.elevMax;
    f32[26] = elevScale;
    f32[27] = tile.texelSize;
    // wireframe at f32 index 28 (byte 112)
    u32[28] = 0;
    // has_detail at f32 index 29 (byte 116)
    u32[29] = tile.detailTexture ? 1 : 0;
    // detail_origin_u at f32 index 30 (byte 120)
    f32[30] = tile.detailOriginU;
    // detail_origin_v at f32 index 31 (byte 124)
    f32[31] = tile.detailOriginV;
    // detail_scale at f32 index 32 (byte 128)
    f32[32] = tile.detailScale;

    this.queue.writeBuffer(tile.uniformBuffer, 0, _uniformBuf);
  }

  render(
    tiles: Iterable<TileRenderState>,
    vp: Float32Array,
    cameraPosX: number, cameraPosY: number, cameraPosZ: number,
    elevScale: number,
  ): void {
    const textureView = this.context.getCurrentTexture().createView();

    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: textureView,
        clearValue: { r: 0.53, g: 0.81, b: 0.92, a: 1.0 }, // sky blue
        loadOp: 'clear',
        storeOp: 'store',
      }],
      depthStencilAttachment: {
        view: this.depthView,
        depthClearValue: 1.0,
        depthLoadOp: 'clear',
        depthStoreOp: 'store',
      },
    });

    pass.setPipeline(this.pipeline);

    for (const tile of tiles) {
      this.writeUniforms(tile, vp, cameraPosX, cameraPosY, cameraPosZ, elevScale);

      pass.setBindGroup(0, tile.bindGroup);

      if (tile.gpuCbt) {
        // GPU CBT path: non-indexed draw via drawIndirect
        pass.setVertexBuffer(0, tile.gpuCbt.vertexBuf);
        pass.drawIndirect(tile.gpuCbt.indirectBuf, 0);
      } else {
        // CPU CBT path: indexed draw
        if (tile.numIndices === 0) continue;
        pass.setVertexBuffer(0, tile.vertexBuffer);
        pass.setIndexBuffer(tile.indexBuffer, 'uint32');
        pass.drawIndexed(tile.numIndices);
      }
    }

    pass.end();
    this.queue.submit([encoder.finish()]);
  }

  /** Create a per-tile uniform buffer. */
  createUniformBuffer(): GPUBuffer {
    return this.device.createBuffer({
      size: UNIFORM_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
  }

  /** Create a bind group for a tile's heightmap texture. */
  createTileBindGroup(uniformBuffer: GPUBuffer, textureView: GPUTextureView, detailView?: GPUTextureView): GPUBindGroup {
    return this.device.createBindGroup({
      layout: this.bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: uniformBuffer } },
        { binding: 1, resource: textureView },
        { binding: 2, resource: this.sampler },
        { binding: 3, resource: detailView ?? this.dummyDetailView },
      ],
    });
  }
}
